import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import ImageAssetView from '../../components/ImageAssetView';
import PageHeader from '../../components/PageHeader';
import FilterBar from '../../components/FilterBar';
import DeleteDialog from '../../../../components/DeleteDialog';
import { useQuery } from '../../../../constants';
import { fetchAllAprenders } from '../../redux/actions/aprender';
import {
  fetchAiAssets,
  createAiAsset,
  updateAiAsset,
  deleteAiAsset,
} from '../../redux/actions/ai';
import { mapAprenderReference } from './aprenderAssets';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  thumbnail: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #eee',
  },
  thumbnailContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  sourceBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const headCells = [
  {
    id: 'image',
    numeric: false,
    disablePadding: false,
    label: 'Image',
    width: '80px',
    disableSorting: true,
    formatter: (row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {row.thumbnailUrl && (
          <img
            src={row.thumbnailUrl || row.imageUrl}
            alt={row.title}
            className="zoomable-thumbnail"
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
            onError={(e) => {
              if (row.imageUrl && e.target.src !== row.imageUrl) {
                e.target.src = row.imageUrl;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        )}
      </div>
    )
  },
  { id: 'title', numeric: false, disablePadding: false, label: 'Title' },
  {
    id: 'actions',
    numeric: true,
    disablePadding: false,
    label: 'Actions',
    disableSorting: true,
    actions: [
      {
        label: 'View',
        action: (row) => { if (row.imageUrl) document.dispatchEvent(new CustomEvent('view-asset', { detail: row })); },
        icon: <VisibilityIcon fontSize="small" />,
        disabled: (row) => !row.imageUrl,
      },
      {
        label: 'Edit',
        action: (row) => document.dispatchEvent(new CustomEvent('edit-asset', { detail: row })),
        icon: <EditIcon fontSize="small" />,
        disabled: () => false,
      },
      {
        label: 'Delete',
        action: (row) => document.dispatchEvent(new CustomEvent('delete-asset', { detail: row })),
        icon: <DeleteForeverIcon fontSize="small" color="secondary" />,
        disabled: () => false,
      }
    ]
  },
];

export default function AiAssets() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const page = useQuery('page') || 1;
  const limit = useQuery('limit') || 25;
  const [search, setSearch] = useState('');

  const { fetching, actionLoading, assets } = useSelector(({ admin }) => admin.ai);
  const aprenders = useSelector(({ admin }) => admin.aprender?.aprenders);

  const [sort, setSort] = useState({ orderBy: 'createdAt', orderDir: 'desc' });
  const [viewMode, setViewMode] = useState('table');
  const [source, setSource] = useState('all');
  const [aprenderPage, setAprenderPage] = useState(1);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Form State
  const [title, setTitle] = useState('');
  const [urlOrKey, setUrlOrKey] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    dispatch(fetchAiAssets(page, limit, search, sort.orderBy, sort.orderDir));
  }, [dispatch, page, limit, search, sort]);

  useEffect(() => {
    if (source === 'native') return;
    dispatch(fetchAllAprenders({
      page: aprenderPage,
      limit,
      search,
      orderBy: 'spanishName',
      orderDir: 'asc',
    }));
  }, [dispatch, source, aprenderPage, limit, search]);

  const handleSearch = (val) => {
    setSearch(val);
    setAprenderPage(1);
  };

  const aprenderView = useMemo(() => {
    if (!aprenders?.docs) return null;
    const needle = search.trim().toLowerCase();
    const docs = aprenders.docs
      .map(mapAprenderReference)
      .filter(Boolean)
      .filter((row) => {
        if (!needle) return true;
        return (
          row.title.toLowerCase().includes(needle) ||
          (row.aliases || []).some((alias) => alias.toLowerCase().includes(needle))
        );
      });
    return { ...aprenders, docs };
  }, [aprenders, search]);

  const handleAddSubmit = async () => {
    if (!title || !file) return;
    const success = await dispatch(createAiAsset(title, '', file));
    if (success) {
      setAddDialogOpen(false);
      setTitle('');
      setUrlOrKey('');
      setFile(null);
      dispatch(fetchAiAssets(page, limit, search));
    }
  };

  const handleEditSubmit = async () => {
    if (!title || !selectedAsset) return;
    const success = await dispatch(updateAiAsset(selectedAsset._id, title, urlOrKey, file));
    if (success) {
      setEditDialogOpen(false);
      setSelectedAsset(null);
      setTitle('');
      setUrlOrKey('');
      setFile(null);
      dispatch(fetchAiAssets(page, limit, search));
    }
  };

  const handleDelete = async () => {
    if (selectedAsset) {
      const success = await dispatch(deleteAiAsset(selectedAsset._id));
      if (success) {
        setDeleteDialogOpen(false);
        setSelectedAsset(null);
        dispatch(fetchAiAssets(page, limit, search));
      }
    }
  };

  const openAddDialog = () => {
    setTitle('');
    setUrlOrKey('');
    setFile(null);
    setAddDialogOpen(true);
  };

  const openEditDialog = (asset) => {
    setSelectedAsset(asset);
    setTitle(asset.title);
    setUrlOrKey(asset.imageKey || asset.imageUrl || '');
    setFile(null);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (asset) => {
    setSelectedAsset(asset);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    const handleEdit = (e) => openEditDialog(e.detail);
    const handleDeleteEvent = (e) => openDeleteDialog(e.detail);
    const handleView = (e) => {
      setViewImageUrl(e.detail.imageUrl);
      setViewDialogOpen(true);
    };

    document.addEventListener('edit-asset', handleEdit);
    document.addEventListener('delete-asset', handleDeleteEvent);
    document.addEventListener('view-asset', handleView);

    return () => {
      document.removeEventListener('edit-asset', handleEdit);
      document.removeEventListener('delete-asset', handleDeleteEvent);
      document.removeEventListener('view-asset', handleView);
    };
  }, []);

  return (
    <div className={classes.root}>
      {fetching && <Loader fetching={fetching} />}

      <PageHeader
        title="Native Assets"
        disableSearch
        options={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            Add Asset
          </Button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearch={handleSearch}
        enableTopics={false}
      />

      <div className={classes.sourceBar}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={source}
          onChange={(_, value) => { if (value) setSource(value); }}
          aria-label="asset source"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="native">Native</ToggleButton>
          <ToggleButton value="aprender">Aprender</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, value) => { if (value) setViewMode(value); }}
          aria-label="asset view"
        >
          <ToggleButton value="table" aria-label="table view">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {(source === 'all' || source === 'native') && (
        assets && assets.docs && assets.docs.length > 0 ? (
          <>
            {source === 'all' && (
              <Typography className={classes.sectionTitle} variant="subtitle1">Native</Typography>
            )}
            <ImageAssetView
              data={assets}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showToggle={false}
              handlePage={(p) => dispatch(fetchAiAssets(p, limit, search, sort.orderBy, sort.orderDir))}
              getImage={(row) => ({
                src: row.thumbnailUrl || row.imageUrl,
                fallback: row.imageUrl,
                alt: row.title,
              })}
              getTitle={(row) => row.title}
              onImageClick={(row) => {
                if (!row.imageUrl) return;
                setViewImageUrl(row.imageUrl);
                setViewDialogOpen(true);
              }}
              actions={[
                {
                  label: 'View',
                  icon: <VisibilityIcon fontSize="small" />,
                  onClick: (row) => {
                    if (!row.imageUrl) return;
                    setViewImageUrl(row.imageUrl);
                    setViewDialogOpen(true);
                  },
                  disabled: (row) => !row.imageUrl,
                },
                {
                  label: 'Edit',
                  icon: <EditIcon fontSize="small" />,
                  onClick: openEditDialog,
                },
                {
                  label: 'Delete',
                  icon: <DeleteForeverIcon fontSize="small" color="secondary" />,
                  onClick: openDeleteDialog,
                },
              ]}
              table={(
                <EnhancedTable
                  data={assets}
                  headCells={headCells}
                  handlePage={(p) => dispatch(fetchAiAssets(p, limit, search, sort.orderBy, sort.orderDir))}
                  orderField={sort.orderBy}
                  orderDirection={sort.orderDir}
                  handleOrder={(s) => setSort(s)}
                />
              )}
            />
          </>
        ) : (
          !fetching && (
            <Typography color="textSecondary" style={{ marginTop: 16 }}>
              No assets found.
            </Typography>
          )
        )
      )}

      {(source === 'all' || source === 'aprender') && (
        aprenderView && aprenderView.docs && aprenderView.docs.length > 0 ? (
          <>
            {source === 'all' && (
              <Typography className={classes.sectionTitle} variant="subtitle1">Aprender</Typography>
            )}
            <ImageAssetView
              data={aprenderView}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showToggle={false}
              handlePage={(p) => setAprenderPage(p)}
              getImage={(row) => ({
                src: row.imageUrl,
                fallback: row.imageUrl,
                alt: row.title,
              })}
              getTitle={(row) => row.title}
              getSourceLabel={() => 'Aprender'}
              onImageClick={(row) => {
                setViewImageUrl(row.imageUrl);
                setViewDialogOpen(true);
              }}
              actions={[
                {
                  label: 'View',
                  icon: <VisibilityIcon fontSize="small" />,
                  onClick: (row) => {
                    setViewImageUrl(row.imageUrl);
                    setViewDialogOpen(true);
                  },
                },
              ]}
              table={(
                <EnhancedTable
                  data={aprenderView}
                  headCells={[
                    headCells[0],
                    { id: 'title', numeric: false, disablePadding: false, label: 'Title', disableSorting: true },
                    {
                      id: 'actions',
                      numeric: true,
                      disablePadding: false,
                      label: 'Actions',
                      disableSorting: true,
                      actions: [
                        {
                          label: 'View',
                          action: (row) => {
                            setViewImageUrl(row.imageUrl);
                            setViewDialogOpen(true);
                          },
                          icon: <VisibilityIcon fontSize="small" />,
                          disabled: (row) => !row.imageUrl,
                        },
                      ],
                    },
                  ]}
                  handlePage={(p) => setAprenderPage(p)}
                  orderField="spanishName"
                  orderDirection="asc"
                  handleOrder={() => {}}
                />
              )}
            />
          </>
        ) : (
          !fetching && (
            <Typography color="textSecondary" style={{ marginTop: 16 }}>
              No Aprender images found.
            </Typography>
          )
        )
      )}

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Asset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Provide a short title (e.g. "CAT"). The title will be used for @-mentions.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Title (e.g. CAT)"
            type="text"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={actionLoading}
            required
          />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, marginBottom: 8 }}>
            Upload Reference Image
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files[0] || null)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSubmit}
            color="primary"
            variant="contained"
            disabled={actionLoading || !title || !file}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Asset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title (e.g. CAT)"
            type="text"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={actionLoading}
            required
          />
          <TextField
            margin="dense"
            label="S3 Key or Image URL"
            type="text"
            fullWidth
            value={urlOrKey}
            onChange={(e) => setUrlOrKey(e.target.value)}
            disabled={actionLoading}
            helperText="Optional if uploading a new image."
          />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, marginBottom: 8 }}>
            Upload New Reference Image (Optional)
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files[0] || null)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
            disabled={actionLoading || !title || !urlOrKey}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleConfirm={handleDelete}
        loading={actionLoading}
        title="Delete Asset"
        content={`Are you sure you want to delete the asset "${selectedAsset?.title}"? It will be removed from the references but the image file will remain in S3.`}
      />

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent style={{ position: 'relative', padding: 0, overflow: 'hidden' }}>
          <IconButton
            style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', color: 'white' }}
            onClick={() => setViewDialogOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <img src={viewImageUrl} alt="Asset reference" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
